import { motion } from "framer-motion";
import {
  Code2,
  Bot,
  ShoppingBag,
  Layers,
  Palette,
  Wrench,
  Boxes,
  Globe,
  ArrowUpRight,
} from "lucide-react";

const services = [
  { n: "01", title: "Web Development", icon: Globe, desc: "Custom, responsive, SEO-optimized sites built with React, Next.js and Vue.", span: "lg:col-span-5 lg:row-span-1" },
  { n: "02", title: "AI Integrations", icon: Bot, desc: "Chatbots, ML, NLP and custom intelligence layered into your product.", span: "lg:col-span-7 lg:row-span-2 bg-gradient-to-br from-nova-emerald/40 to-transparent" },
  { n: "03", title: "Web Applications", icon: Code2, desc: "Full-stack apps and PWAs with real-time data and scalable backends.", span: "lg:col-span-5" },
  { n: "04", title: "E-Commerce", icon: ShoppingBag, desc: "Online stores with payment gateways, inventory and analytics.", span: "lg:col-span-4" },
  { n: "05", title: "SaaS Development", icon: Layers, desc: "Multi-tenant, cloud-native architecture engineered to scale.", span: "lg:col-span-4" },
  { n: "06", title: "UI / UX Design", icon: Palette, desc: "Wireframing, prototyping, and pixel-perfect interface design.", span: "lg:col-span-4" },
  { n: "07", title: "Maintenance & Support", icon: Wrench, desc: "Ongoing updates, bug fixes and performance tuning.", span: "lg:col-span-7" },
  { n: "08", title: "Custom Software", icon: Boxes, desc: "Tailored solutions, legacy modernization and integrations.", span: "lg:col-span-5" },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-32 lg:py-44 px-6 lg:px-10 overflow-hidden">
      <div className="orb orb-gold w-[500px] h-[500px] -top-40 -left-40" />
      <div className="orb orb-violet w-[400px] h-[400px] top-1/2 -right-40 opacity-25" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="mono-label mb-4">[ 01 — What we do ]</div>
            <h2 className="font-display font-bold tracking-tighter text-4xl lg:text-6xl text-nova-text leading-[0.95]">
              Services built to <span className="italic font-light">elevate</span> your brand.
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-nova-mute text-lg leading-relaxed self-end">
            Every engagement is bespoke. We bring engineering rigor, design taste and AI fluency
            to deliver outcomes that look — and feel — expensive.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:auto-rows-[220px]">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.a
                href="#contact"
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: i * 0.04 }}
                data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`group relative border border-nova-line bg-nova-surface p-8 lg:p-10 overflow-hidden hover:border-nova-gold/40 transition-all duration-500 flex flex-col justify-between ${s.span}`}
              >
                <div className="flex items-start justify-between">
                  <Icon className="w-7 h-7 text-nova-gold opacity-80 group-hover:scale-110 transition-transform" strokeWidth={1.2} />
                  <span className="mono-label text-nova-mute">{s.n}</span>
                </div>
                <div>
                  <h3 className="font-display font-medium text-2xl lg:text-3xl text-nova-text mb-3 tracking-tight">
                    {s.title}
                  </h3>
                  <p className="text-nova-mute text-sm lg:text-base leading-relaxed max-w-md">
                    {s.desc}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-nova-mute group-hover:text-nova-gold transition-colors">
                    Discuss
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>
                </div>
                <div className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full bg-nova-gold/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
